import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

/* ── helpers ── */
function getCart() {
  try { return JSON.parse(localStorage.getItem("dar_oulmes_cart") || "[]"); }
  catch { return []; }
}
function saveCart(cart) {
  localStorage.setItem("dar_oulmes_cart", JSON.stringify(cart));
  window.dispatchEvent(new Event("cart-updated"));
}

/* ── SVG icons ── */
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const TruckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/>
    <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const ShoppingBagIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);

/* ── animation variants ── */
const itemVariants = {
  hidden:  { opacity: 0, y: 20, scale: 0.98 },
  visible: { opacity: 1, y: 0,  scale: 1, transition: { type: "spring", stiffness: 120, damping: 18 } },
  exit:    { opacity: 0, x: -60, scale: 0.95, transition: { duration: 0.22 } },
};

export default function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(getCart);
  const [coupon, setCoupon]     = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [discount, setDiscount]  = useState(0);

  /* sync to localStorage on every change */
  useEffect(() => { saveCart(cart); }, [cart]);

  const updateQty = (id, delta) =>
    setCart(prev => prev.map(item =>
      item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
    ));

  const removeItem = (id) =>
    setCart(prev => prev.filter(i => i.id !== id));

  const clearAll = () => setCart([]);

  const applyCoupon = () => {
    if (coupon.toUpperCase() === "DAROULMES10") {
      setDiscount(10);
      setCouponMsg({ ok: true, text: t("cart.coupon_applied") });
    } else {
      setDiscount(0);
      setCouponMsg({ ok: false, text: t("cart.coupon_invalid") });
    }
  };

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax      = Math.round(subtotal * 0.06);
  const discAmt  = Math.round(subtotal * discount / 100);
  const total    = subtotal + tax - discAmt;
  const itemCount = cart.reduce((s, i) => s + i.quantity, 0);

  /* navigate to checkout, passing cart + totals via state */
  const goToCheckout = () => {
    navigate("/checkout", {
      state: { cart, subtotal, tax, discAmt, total },
    });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f0e8", fontFamily: "'Cormorant Garamond', Georgia, serif", overflowX: "hidden", position: "relative" }}>
      <style>{CSS}</style>

      {/* Decorative background */}
      <div className="cp-bg" aria-hidden="true" />
      <div className="cp-glow cp-glow-1" aria-hidden="true" />
      <div className="cp-glow cp-glow-2" aria-hidden="true" />

      {/* Gold dust particles */}
      <div className="cp-dust-wrap" aria-hidden="true">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="cp-dust" style={{
            left: `${(i * 8.3) % 100}%`,
            animationDuration: `${9 + (i % 5) * 1.5}s`,
            animationDelay: `${i * 0.8}s`,
            width: i % 3 === 0 ? "3px" : "2px",
            height: i % 3 === 0 ? "3px" : "2px",
          }} />
        ))}
      </div>

      <div className="cp-container">

        {/* ── HERO ── */}
        <motion.div
          className="cp-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="cp-eyebrow">
            <div className="cp-eyebrow-line" />
            <span>{t("cart.eyebrow")}</span>
          </div>

          <h1 className="cp-hero-title">
            {t("cart.hero_title_line1")}<br />
            <em style={{ color: "#c9a84c", fontStyle: "italic", fontWeight: 300 }}>{t("cart.hero_title_em")}</em>
            <br />
            <span style={{ fontWeight: 600 }}>{t("cart.hero_title_line3")}</span>
          </h1>

          <p className="cp-hero-sub">
            {t("cart.hero_sub", { count: itemCount, noun: itemCount === 1 ? t("cart.piece") : t("cart.pieces") })}
          </p>
        </motion.div>

        {/* Gold shimmer divider */}
        <div className="cp-gold-bar" />

        {/* ── MAIN GRID ── */}
        <div className="cp-layout">

          {/* LEFT – cart items */}
          <div className="cp-items-col">
            <div className="cp-items-header">
              <span className="cp-items-count">{cart.length} {t("cart.items_in_selection")}</span>
              {cart.length > 0 && (
                <button className="cp-clear-btn" onClick={clearAll}>{t("cart.clear_all")}</button>
              )}
            </div>

            {/* Empty state */}
            {cart.length === 0 && (
              <motion.div
                className="cp-empty"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="cp-empty-icon" style={{ color: "rgba(201,168,76,.35)" }}>
                  <ShoppingBagIcon />
                </div>
                <h3>{t("cart.empty_title")}</h3>
                <p>{t("cart.empty_desc")}</p>
                <button className="cp-shop-btn" onClick={() => navigate("/products")}>
                  {t("cart.explore_collection")} <ArrowRightIcon />
                </button>
              </motion.div>
            )}

            {/* Cart items */}
            <AnimatePresence mode="popLayout">
              {cart.map((item) => (
                <motion.div
                  key={item.id}
                  className="cp-item"
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                >
                  {/* Gold accent left bar */}
                  <div className="cp-item-bar" />

                  {/* Image */}
                  <div className="cp-item-img">
                    {item.img
                      ? <img src={item.img} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div className="cp-item-img-placeholder">
                          <span style={{ fontSize: 28, color: "rgba(201,168,76,.4)" }}>◈</span>
                        </div>
                    }
                    {item.tag && <span className="cp-item-tag">{item.tag}</span>}
                  </div>

                  {/* Info */}
                  <div className="cp-item-info">
                    {item.size && <span className="cp-item-meta">{item.size}</span>}
                    <h3 className="cp-item-name">{item.name}</h3>
                    <p className="cp-item-unit">{parseFloat(item.price).toFixed(2)} MAD {t("cart.per_piece")}</p>

                    {/* Qty control */}
                    <div className="cp-qty">
                      <button className="cp-qty-btn" onClick={() => updateQty(item.id, -1)}>−</button>
                      <span className="cp-qty-num">{item.quantity}</span>
                      <button className="cp-qty-btn" onClick={() => updateQty(item.id, 1)}>+</button>
                    </div>
                  </div>

                  {/* Price + remove */}
                  <div className="cp-item-right">
                    <span className="cp-item-total">{(item.price * item.quantity).toFixed(2)} MAD</span>
                    <button className="cp-remove-btn" onClick={() => removeItem(item.id)}>
                      <TrashIcon /> {t("cart.remove_btn")}
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Reassurance strip */}
            {cart.length > 0 && (
              <motion.div
                className="cp-reassurance"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {[
                  { icon: <TruckIcon />,  text: t("cart.free_delivery") },
                  { icon: <ShieldIcon />, text: t("cart.authenticity_guaranteed") },
                  { icon: <LockIcon />,   text: t("cart.secure_checkout") },
                ].map(({ icon, text }) => (
                  <div key={text} className="cp-reassurance-item">
                    <span style={{ color: "#c9a84c" }}>{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </div>

          {/* RIGHT – order summary */}
          {cart.length > 0 && (
            <motion.div
              className="cp-summary"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.55 }}
            >
              <h2 className="cp-summary-title">{t("cart.order_summary")}</h2>

              {/* Mini item list */}
              <div className="cp-summary-items">
                {cart.map(item => (
                  <div key={item.id} className="cp-summary-item">
                    <span className="cp-summary-item-name">
                      {item.name} <span style={{ color: "#c9a84c" }}>×{item.quantity}</span>
                    </span>
                    <span className="cp-summary-item-price">{(item.price * item.quantity).toFixed(2)} MAD</span>
                  </div>
                ))}
              </div>

              {/* Breakdown */}
              <div className="cp-summary-breakdown">
                {[
                  { label: t("cart.subtotal"),   val: `${subtotal.toFixed(2)} MAD`, dim: false },
                  { label: t("cart.delivery"),   val: t("cart.free"),  dim: false, green: true },
                  { label: t("cart.tax"),   val: `${tax.toFixed(2)} MAD`,      dim: true },
                  ...(discount > 0 ? [{ label: t("cart.discount", { percent: discount }), val: `-${discAmt.toFixed(2)} MAD`, red: true }] : []),
                ].map(({ label, val, dim, green, red }) => (
                  <div key={label} className="cp-summary-row">
                    <span style={{ color: "#8a7a55" }}>{label}</span>
                    <span style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 600,
                      fontSize: 15,
                      color: green ? "#2d6a4f" : red ? "#c0392b" : "#1a1a12"
                    }}>{val}</span>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="cp-coupon">
                <input
                  className="cp-coupon-input"
                  placeholder={t("cart.coupon_placeholder")}
                  value={coupon}
                  onChange={e => { setCoupon(e.target.value); setCouponMsg(null); }}
                  onKeyDown={e => e.key === "Enter" && applyCoupon()}
                />
                <button className="cp-coupon-btn" onClick={applyCoupon}>{t("cart.apply")}</button>
              </div>
              {couponMsg && (
                <p style={{ fontSize: 12, fontFamily: "'EB Garamond', serif", marginTop: 6, color: couponMsg.ok ? "#2d6a4f" : "#c0392b" }}>
                  {couponMsg.text}
                </p>
              )}

              {/* Total */}
              <div className="cp-total-row">
                <span className="cp-total-label">{t("cart.total_label")}</span>
                <span className="cp-total-amount">{total.toFixed(2)} MAD</span>
              </div>

              {/* CHECKOUT button — navigates to CheckoutPage with cart state */}
              <motion.button
                className="cp-checkout-btn"
                whileHover={{ y: -2, boxShadow: "0 14px 36px rgba(201,168,76,.5)" }}
                whileTap={{ scale: 0.98 }}
                onClick={goToCheckout}
              >
                <span style={{ position: "relative", zIndex: 1 }}>{t("cart.proceed_to_checkout")}</span>
                <span className="cp-checkout-shine" aria-hidden="true" />
                <ArrowRightIcon />
              </motion.button>

              <p className="cp-checkout-note">
                {t("cart.checkout_note")}
              </p>

              {/* Trust badges */}
              <div className="cp-trust">
                <div className="cp-trust-item"><LockIcon /> {t("cart.trust_secure")}</div>
                <div className="cp-trust-divider" />
                <div className="cp-trust-item"><ShieldIcon /> {t("cart.trust_protected")}</div>
                <div className="cp-trust-divider" />
                <div className="cp-trust-item"><TruckIcon /> {t("cart.trust_free_delivery")}</div>
              </div>

              {/* Payment icons */}
              <div className="cp-payment-icons">
                <span style={{ fontFamily: "sans-serif", fontWeight: 900, fontSize: 13, color: "#1a1f71" }}>VISA</span>
                <svg width="28" height="18" viewBox="0 0 30 20">
                  <circle cx="11" cy="10" r="9" fill="#EB001B" opacity=".9"/>
                  <circle cx="19" cy="10" r="9" fill="#F79E1B" opacity=".9"/>
                </svg>
                <span style={{ fontFamily: "sans-serif", fontWeight: 700, fontSize: 11, color: "#003087" }}>
                  <span style={{ color: "#009cde" }}>Pay</span>Pal
                </span>
              </div>
            </motion.div>
          )}
        </div>

        {/* ── FOOTER BANNER ── */}
        <motion.div
          className="cp-banner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="cp-banner-glow" aria-hidden="true" />
          <div className="cp-banner-content">
            <div>
              <h3 className="cp-banner-title">
                {t("cart.banner_title_prefix")} <em style={{ color: "#c9a84c", fontStyle: "italic" }}>{t("cart.banner_title_em")}</em>
              </h3>
              <p className="cp-banner-sub">{t("cart.banner_sub")}</p>
            </div>
            <button className="cp-banner-btn" onClick={() => navigate("/products")}>
              {t("cart.continue_shopping")}
            </button>
          </div>
        </motion.div>

      </div>
    </div>
  );
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Cinzel:wght@400;500;600&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Background ── */
  .cp-bg {
    position: fixed; inset: 0; pointer-events: none; z-index: 0; opacity: 0.04;
    background-image:
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cpolygon points='60,5 115,35 115,85 60,115 5,85 5,35' fill='none' stroke='%23c9a84c' stroke-width='0.8'/%3E%3Ccircle cx='60' cy='60' r='8' fill='none' stroke='%23c9a84c' stroke-width='0.4'/%3E%3C/svg%3E");
    background-size: 120px 120px;
  }
  .cp-glow {
    position: fixed; pointer-events: none; z-index: 0; border-radius: 50%; filter: blur(90px);
  }
  .cp-glow-1 {
    width: 500px; height: 500px; top: -80px; right: -80px;
    background: radial-gradient(circle, rgba(201,168,76,.09) 0%, transparent 65%);
  }
  .cp-glow-2 {
    width: 380px; height: 380px; bottom: 40px; left: -60px;
    background: radial-gradient(circle, rgba(45,90,61,.18) 0%, transparent 65%);
  }

  /* Dust particles */
  .cp-dust-wrap { position: fixed; inset: 0; pointer-events: none; z-index: 0; }
  .cp-dust {
    position: absolute; bottom: -5px; border-radius: 50%;
    background: #c9a84c; opacity: 0;
    animation: cpFloatDust linear infinite;
  }
  @keyframes cpFloatDust {
    0%   { transform: translateY(0) translateX(0); opacity: 0; }
    10%  { opacity: 0.5; }
    90%  { opacity: 0.2; }
    100% { transform: translateY(-100vh) translateX(20px); opacity: 0; }
  }

  /* ── Container ── */
  .cp-container {
    position: relative; z-index: 1;
    max-width: 1200px; margin: 0 auto; padding: 0 48px 80px;
  }

  /* ── Hero ── */
  .cp-hero { padding: 56px 0 36px; }
  .cp-eyebrow {
    display: flex; align-items: center; gap: 16px; margin-bottom: 22px;
  }
  .cp-eyebrow-line {
    height: 1px; width: 48px;
    background: linear-gradient(90deg, transparent, #c9a84c);
  }
  .cp-eyebrow span {
    font-family: 'Cinzel', serif; font-size: 9px;
    letter-spacing: 4px; color: #c9a84c;
  }
  .cp-hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(52px, 8vw, 90px);
    font-weight: 300; line-height: 0.95;
    color: #1a1a12; letter-spacing: -1px;
    margin-bottom: 18px;
  }
  .cp-hero-sub {
    font-family: 'EB Garamond', serif;
    font-size: 15px; color: #8a7a55; letter-spacing: 1px;
  }

  /* Gold divider */
  .cp-gold-bar {
    height: 1px; margin: 0 0 36px;
    background: linear-gradient(90deg, transparent, #c9a84c 30%, #f5d88a 50%, #c9a84c 70%, transparent);
    background-size: 200% auto;
    animation: shimmerBar 3.5s linear infinite;
    opacity: 0.45;
  }
  @keyframes shimmerBar { 0%{background-position:-200% center} 100%{background-position:200% center} }

  /* ── Layout ── */
  .cp-layout {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 32px;
    align-items: start;
  }
  @media(max-width:900px){
    .cp-layout { grid-template-columns: 1fr; }
    .cp-container { padding: 0 20px 60px; }
  }

  /* ── Items column ── */
  .cp-items-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 22px;
  }
  .cp-items-count {
    font-family: 'Cinzel', serif; font-size: 10px;
    letter-spacing: 3px; color: #8a7a55;
  }
  .cp-clear-btn {
    font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 2px;
    color: #b09070; background: none; border: 1px solid rgba(176,144,112,.3);
    padding: 5px 14px; border-radius: 30px; cursor: pointer;
    transition: color .2s, border-color .2s;
  }
  .cp-clear-btn:hover { color: #c0392b; border-color: #c0392b; }

  /* Empty state */
  .cp-empty {
    text-align: center; padding: 80px 40px;
    border: 2px dashed rgba(201,168,76,.28); border-radius: 24px;
    background: rgba(255,252,244,.6);
  }
  .cp-empty-icon { font-size: 44px; margin-bottom: 20px; opacity: .35; display: flex; justify-content: center; }
  .cp-empty h3 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px; color: #5a4e35; margin-bottom: 10px;
  }
  .cp-empty p { color: #8a7a55; font-size: 15px; margin-bottom: 28px; }
  .cp-shop-btn {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 13px 28px; border-radius: 50px;
    background: linear-gradient(135deg, #c9a84c, #a87c2a);
    border: none; color: #fff;
    font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 2.5px;
    cursor: pointer; box-shadow: 0 6px 22px rgba(201,168,76,.38);
    transition: transform .2s, box-shadow .2s;
  }
  .cp-shop-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(201,168,76,.48); }

  /* ── Cart item ── */
  .cp-item {
    position: relative; display: grid;
    grid-template-columns: 110px 1fr auto;
    gap: 22px; align-items: center;
    background: rgba(255,252,244,.88);
    border: 1px solid rgba(201,168,76,.18);
    border-radius: 20px; padding: 22px;
    margin-bottom: 16px;
    backdrop-filter: blur(8px);
    box-shadow: 0 4px 28px rgba(30,24,10,.06);
    transition: box-shadow .3s, transform .3s, border-color .3s;
    overflow: hidden;
  }
  .cp-item:hover {
    box-shadow: 0 14px 50px rgba(30,24,10,.12);
    transform: translateY(-3px);
    border-color: rgba(201,168,76,.38);
  }
  .cp-item-bar {
    position: absolute; top: 0; left: 0;
    width: 3px; height: 100%;
    background: linear-gradient(180deg, #c9a84c, #a87c2a);
    border-radius: 20px 0 0 20px; opacity: 0;
    transition: opacity .3s;
  }
  .cp-item:hover .cp-item-bar { opacity: 1; }

  .cp-item-img {
    position: relative; width: 110px; height: 110px;
    border-radius: 14px; overflow: hidden;
    background: linear-gradient(135deg, #e8d8a0, rgba(201,168,76,.15));
    flex-shrink: 0;
  }
  .cp-item-img img { transition: transform .4s; }
  .cp-item:hover .cp-item-img img { transform: scale(1.06); }
  .cp-item-img-placeholder {
    width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  }
  .cp-item-tag {
    position: absolute; top: 8px; right: 8px;
    background: linear-gradient(135deg, #c9a84c, #a87c2a);
    color: #fff; font-family: 'Cinzel', serif;
    font-size: 7px; letter-spacing: 1.5px; padding: 3px 8px; border-radius: 20px;
  }

  .cp-item-meta {
    font-family: 'Cinzel', serif; font-size: 9px;
    letter-spacing: 2px; color: #c9a84c; margin-bottom: 4px; display: block;
  }
  .cp-item-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px; font-weight: 600; color: #1a1a12;
    line-height: 1.2; margin-bottom: 5px;
  }
  .cp-item-unit {
    font-size: 13px; color: #8a7a55; letter-spacing: .5px; margin-bottom: 16px;
  }

  /* Qty */
  .cp-qty {
    display: inline-flex; align-items: center;
    border: 1px solid rgba(201,168,76,.35); border-radius: 50px; overflow: hidden;
    background: #f8f3e8;
  }
  .cp-qty-btn {
    width: 34px; height: 34px; border: none; background: transparent;
    font-size: 18px; color: #5a4e35; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background .2s, color .2s;
  }
  .cp-qty-btn:hover { background: #c9a84c; color: #fff; }
  .cp-qty-num {
    padding: 0 14px;
    font-family: 'Cinzel', serif; font-size: 13px; font-weight: 600; color: #1a1a12;
    min-width: 34px; text-align: center;
    border-left: 1px solid rgba(201,168,76,.25);
    border-right: 1px solid rgba(201,168,76,.25);
  }

  .cp-item-right {
    display: flex; flex-direction: column; align-items: flex-end; gap: 14px;
  }
  .cp-item-total {
    font-family: 'Cormorant Garamond', serif;
    font-size: 24px; font-weight: 700; color: #1a1a12;
  }
  .cp-remove-btn {
    display: flex; align-items: center; gap: 5px;
    background: none; border: 1px solid rgba(176,144,112,.28);
    border-radius: 20px; padding: 5px 12px;
    font-family: 'Cinzel', serif; font-size: 8px;
    letter-spacing: 1.5px; color: #b09070; cursor: pointer;
    transition: all .2s;
  }
  .cp-remove-btn:hover { color: #c0392b; border-color: #c0392b; background: rgba(192,57,43,.05); }

  /* Reassurance */
  .cp-reassurance {
    display: flex; flex-wrap: wrap; gap: 24px;
    margin-top: 28px; padding: 22px 28px;
    background: rgba(255,252,244,.6); border-radius: 16px;
    border: 1px solid rgba(201,168,76,.14); backdrop-filter: blur(6px);
  }
  .cp-reassurance-item {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; color: #8a7a55; font-family: 'EB Garamond', serif;
  }

  /* ── Summary panel ── */
  .cp-summary {
    background: rgba(255,252,244,.94);
    border: 1px solid rgba(201,168,76,.22);
    border-radius: 24px; padding: 32px 28px;
    backdrop-filter: blur(14px);
    box-shadow: 0 10px 52px rgba(30,24,10,.1);
    position: sticky; top: 88px;
  }
  .cp-summary-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 27px; font-weight: 600; color: #1a1a12;
    margin-bottom: 22px; padding-bottom: 18px;
    border-bottom: 1px solid rgba(201,168,76,.22);
  }
  .cp-summary-items { margin-bottom: 18px; }
  .cp-summary-item {
    display: flex; justify-content: space-between;
    margin-bottom: 9px; gap: 10px;
  }
  .cp-summary-item-name {
    font-family: 'EB Garamond', serif; font-size: 14px; color: #5a4e35;
    flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .cp-summary-item-price {
    font-family: 'Cormorant Garamond', serif; font-size: 14px; font-weight: 600; color: #1a1a12; flex-shrink: 0;
  }
  .cp-summary-breakdown {
    border-top: 1px solid rgba(201,168,76,.2); padding-top: 16px; margin-bottom: 6px;
  }
  .cp-summary-row {
    display: flex; justify-content: space-between;
    align-items: center; margin-bottom: 10px;
    font-family: 'EB Garamond', serif; font-size: 15px;
  }

  /* Coupon */
  .cp-coupon {
    display: flex; margin: 22px 0 4px;
    border: 1.5px solid rgba(201,168,76,.3); border-radius: 12px; overflow: hidden;
    background: #f8f3e8;
  }
  .cp-coupon-input {
    flex: 1; padding: 12px 16px; border: none; background: transparent;
    font-family: 'EB Garamond', serif; font-size: 14px; color: #3a3220; outline: none;
  }
  .cp-coupon-input::placeholder { color: #b0a080; }
  .cp-coupon-btn {
    padding: 12px 18px; border: none; background: #c9a84c; color: #fff;
    font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 2px; cursor: pointer;
    transition: background .2s;
  }
  .cp-coupon-btn:hover { background: #a87c2a; }

  /* Total */
  .cp-total-row {
    display: flex; justify-content: space-between; align-items: baseline;
    padding: 18px 0 0; margin-top: 10px;
    border-top: 2px solid #c9a84c;
  }
  .cp-total-label {
    font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 2px; color: #1a1a12;
  }
  .cp-total-amount {
    font-family: 'Cormorant Garamond', serif;
    font-size: 36px; font-weight: 700; color: #1a1a12;
  }

  /* Checkout button */
  .cp-checkout-btn {
    position: relative; overflow: hidden;
    width: 100%; margin-top: 22px; padding: 17px;
    background: linear-gradient(135deg, #c9a84c 0%, #a87c2a 100%);
    border: none; border-radius: 14px; color: #fff;
    font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 3px;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px;
    box-shadow: 0 8px 28px rgba(201,168,76,.42);
    transition: box-shadow .3s, transform .2s;
  }
  .cp-checkout-shine {
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.18), transparent);
    transform: translateX(-100%);
    transition: transform .55s;
  }
  .cp-checkout-btn:hover .cp-checkout-shine { transform: translateX(100%); }

  .cp-checkout-note {
    font-family: 'EB Garamond', serif; font-size: 12px; color: #8a7a55;
    text-align: center; margin-top: 12px; line-height: 1.6;
  }

  /* Trust */
  .cp-trust {
    display: flex; align-items: center; justify-content: center; gap: 14px;
    margin-top: 20px; padding-top: 18px;
    border-top: 1px solid rgba(201,168,76,.2);
  }
  .cp-trust-item {
    display: flex; align-items: center; gap: 5px;
    font-size: 11px; color: #8a7a55; letter-spacing: .5px;
  }
  .cp-trust-divider { width: 1px; height: 14px; background: rgba(201,168,76,.3); }
  .cp-payment-icons {
    display: flex; align-items: center; justify-content: center; gap: 14px;
    margin-top: 16px; opacity: .65;
  }

  /* ── Footer banner ── */
  .cp-banner {
    position: relative; margin-top: 52px; overflow: hidden;
    border-radius: 24px; padding: 48px 52px;
    background: linear-gradient(120deg, #132918 0%, #1f3d2b 55%, #254a35 100%);
    border: 1px solid rgba(201,168,76,.25);
    box-shadow: 0 18px 64px rgba(19,41,24,.35);
  }
  .cp-banner-glow {
    position: absolute; right: -60px; top: -60px;
    width: 260px; height: 260px; border-radius: 50%;
    background: radial-gradient(circle, rgba(201,168,76,.12) 0%, transparent 70%);
    pointer-events: none;
  }
  .cp-banner-content {
    position: relative; z-index: 1;
    display: flex; align-items: center; justify-content: space-between; gap: 24px; flex-wrap: wrap;
  }
  .cp-banner-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 34px; font-weight: 300; color: #f8f3e8; line-height: 1.2; margin-bottom: 8px;
  }
  .cp-banner-sub { font-size: 14px; color: rgba(248,243,232,.55); letter-spacing: .5px; }
  .cp-banner-btn {
    background: linear-gradient(135deg, #c9a84c, #a87c2a);
    border: none; border-radius: 50px;
    padding: 14px 32px; color: #fff;
    font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 2px;
    cursor: pointer; white-space: nowrap;
    box-shadow: 0 6px 20px rgba(201,168,76,.4);
    transition: transform .2s, box-shadow .2s;
  }
  .cp-banner-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(201,168,76,.55); }

  @media(max-width:600px){
    .cp-item { grid-template-columns: 80px 1fr auto; gap: 14px; padding: 16px; }
    .cp-item-img { width: 80px; height: 80px; }
    .cp-hero-title { font-size: 52px; }
    .cp-banner { padding: 32px 24px; }
  }
`;